import _ from 'lodash';
import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { of, Subject, throwError, } from 'rxjs';
import { Store } from '@ngrx/store';

import { openDialog } from 'app/dialog/features/dialog.actions';
import { catchError, filter, first, map, takeUntil } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  public imageDataUrlSubject: Subject<any> = new Subject();
  public imageSubject: Subject<any> = new Subject();
  public aspectRatio = {
    default: 2
  };

  public loading: WritableSignal<any> = signal(false);
  public loadingClasses: Signal<any> = computed(() => {
    const classes = {
      disabled: this.loading()
    }
    return classes;
  });

  constructor(
    private store: Store,
    private http: HttpClient,
    private domSanitizer: DomSanitizer,
    private sharedService: SharedService
  ) { }

  cropper(initialState) {
    const action = openDialog({
      payload: {
        name: 'cropper',
        options: {
          size: 'large',
          modalClass:'modal--paybee modal-lg',
          containerClass:'mdb-modal-backdrop-paybee',
          ignoreBackdropClick: true,
        },
        initialState
      }
    });
    this.store.dispatch(action);
  }

  downloadImageBlob(url) {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = function() {
        let canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        let context = canvas.getContext('2d');
        context.drawImage(img, 0, 0);

        canvas.toBlob(function(blob) {
          resolve(blob);
        }, 'image/png');
      }
      img.onerror = function() {
        reject(null);
      }
    });
  }

  getImageBase64(url) {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = function() {
        let canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        let context = canvas.getContext('2d');
        context.drawImage(img, 0, 0);

        const dataUrl = canvas.toDataURL("image/png");
        const base64Image = dataUrl.replace(/^data:image\/(png|jpg);base64,/, "");
        resolve(base64Image);
      }
      img.onerror = function() {
        reject(null);
      }
    });
  }

  uploadImageByBlob(id, blob, filename, isMulti = false) {
    var body = new FormData();
    body.append('image', blob, filename);
    body.append('access_token', this.sharedService.token);
    this.loading.set(true)
    return this.http.post('/api/uploadImage', body).pipe(
      first(),
      catchError(() => {
        return of(false);
      }),
    ).subscribe(result => {
      this.imageSubject.next({ id, result, isMulti });
      this.loading.set(false)
    });
  }

  uploadImageByBlobAndCrop(blob, filename, imageType = 0) { // 0 for 2x1, 1 for 1x1
    var body = new FormData();
    body.append('image', blob, filename);
    body.append('access_token', this.sharedService.token);
    body.append('p', JSON.stringify({ imageType: imageType }));
    return this.http.post('/api/cropAndUpload', body).pipe(
      first()
    );
  }

  uploadImageByFileAndCrop(id, file) {
    var body = new FormData();
    body.append('image', file);
    body.append('access_token', this.sharedService.token);
    return this.http.post('/api/cropAndUpload', body).pipe(
      first()
    );
  }

  uploadImageByFile (file) {
    var body = new FormData();
    body.append('image', file);
    body.append('access_token', this.sharedService.token);
    return this.http.post('/api/uploadImage', body).pipe(
      first()
    );
  }

  updateAspectRatio(aspectRatio, pageName = 'default') {
    this.aspectRatio[pageName] = aspectRatio;
  }

  initializeImageControl(id, form, takeUntilSubject, pageName = 'default') {

    form.get('imageFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(takeUntilSubject)
    ).subscribe(value => {
      let aspectRatio = this.aspectRatio.default;

      if (pageName && this.aspectRatio[pageName]) {
        aspectRatio = this.aspectRatio[pageName];
      }

      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.cropper({
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id,
        name: value.name,
        type: value.type,
        aspectRatio
      });
    });

    this.imageDataUrlSubject.pipe(
      filter((data) => data.id === id),
      takeUntil(takeUntilSubject)
    ).subscribe(({ result }) => {
      form.get('imageDataUrl').setValue(result);
    });

    this.imageSubject.pipe(
      filter((data) => data.id === id),
      takeUntil(takeUntilSubject)
    ).subscribe(({ result }) => {
      if (result === false) {
        this.resetImageControl(form);
      } else {
        form.get('imageUrl').setValue(result.imageUrl);
      }
    });
  }

  resetImageControl(form) {
    form.get('imageFile').setValue(null);
    form.get('imageDataUrl').setValue('/images/blank_2x1.png');
    form.get('imageUrl').setValue('/images/blank_2x1.png');
  }
}
